import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AISHA_API_BASE } from '../../constants';
import {
  XMarkIcon as CloseIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  ShoppingBagIcon,
} from '../icons/HeroIcons';
import ProdigiOrderModal from './ProdigiOrderModal';

/**
 * Prodigi 購入モーダル（位置調整エディタ）
 *
 * Prodigi Print API には座標指定もモック生成も無い。Prodigiダッシュボードの
 * 「Add image」は、商品の推奨px(printAreaSizes)のキャンバスに配置を焼き込んで
 * 1枚の画像として注文に添付する。ここではそれと同じエディタを AISHA 内に作る。
 * - バリエーション(axes)選択 → SKU確定 → 推奨px取得(/prodigi/print-area/{sku}/)
 * - 生成画像をドラッグ/拡縮/回転で配置（normalized transform で保持）
 * - 確定で推奨pxに合成（プレビュー表示）
 * 注文/決済(実課金)は次段。ここでは合成と価格提示まで。
 */

interface ProdigiVariant {
  sku: string;
  attrs?: Record<string, string>;
  price?: number;
  currency?: string;
  desc?: string;
}

interface ProdigiApiConfig {
  product_type?: string;
  category?: string;
  subcategory?: string;
  description?: string;
  axes?: Record<string, string[]>;
  variants?: ProdigiVariant[];
  published_skus?: string[];
  image_url?: string;
}

interface ProdigiItem {
  id: string | number;
  goodsId?: number;   // 数値のGoodsManagement id（価格FX取得・注文紐付けに使用）
  displayName: string;
  finalPrice: number;
  icon_url?: string | null;
  sample_image_url?: string | null;
  apiConfig?: ProdigiApiConfig;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;      // ユーザーの生成画像（公開GCS URL）
  item: ProdigiItem;     // GoodsManagement(prodigi) 由来
  userId?: string;
  libraryImageId?: string;
  carName?: string;
  onCreated?: () => void; // 作成成功時（履歴リフレッシュ用）
}

interface Transform {
  cx: number;   // 中心x（キャンバス幅に対する割合 0..1）
  cy: number;   // 中心y（キャンバス高に対する割合 0..1）
  scale: number; // 画像幅がキャンバス幅に占める割合
  rot: number;  // 回転（度）
}

const DEFAULT_TF: Transform = { cx: 0.5, cy: 0.5, scale: 1, rot: 0 };

// 文字を幅に合わせて折返す（改行対応・CJKは文字単位で折返す）
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const raw of text.split('\n')) {
    if (raw === '') { out.push(''); continue; }
    let line = '';
    for (const ch of Array.from(raw)) {
      const test = line + ch;
      if (ctx.measureText(test).width > maxW && line) {
        out.push(line);
        line = ch;
      } else {
        line = test;
      }
    }
    out.push(line);
  }
  return out;
}

// ポストカード用: 写真面の下部にメッセージ帯を焼き込む
function drawMessage(ctx: CanvasRenderingContext2D, cw: number, ch: number, text: string) {
  const pad = Math.round(cw * 0.045);
  const fontSize = Math.max(12, Math.round(ch * 0.05));
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const lines = wrapLines(ctx, text, cw - pad * 2);
  const lineH = Math.round(fontSize * 1.3);
  const bandH = lines.length * lineH + pad * 1.5;
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(0, ch - bandH, cw, bandH);
  ctx.fillStyle = '#ffffff';
  let y = ch - bandH + pad * 0.75 + fontSize;
  for (const ln of lines) {
    ctx.fillText(ln, cw / 2, y);
    y += lineH;
  }
}

// キャンバス(px)に画像をnormalized transformで描画（プレビュー・最終で共通）
function drawComposite(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  img: HTMLImageElement,
  tf: Transform,
  message?: string,
) {
  ctx.clearRect(0, 0, cw, ch);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cw, ch);
  const imgW = tf.scale * cw;
  const ratio = img.naturalHeight / img.naturalWidth || 1;
  const imgH = imgW * ratio;
  ctx.save();
  ctx.translate(tf.cx * cw, tf.cy * ch);
  ctx.rotate((tf.rot * Math.PI) / 180);
  ctx.drawImage(img, -imgW / 2, -imgH / 2, imgW, imgH);
  ctx.restore();
  if (message && message.trim()) {
    drawMessage(ctx, cw, ch, message.trim());
  }
}

const ProdigiPurchaseModal: React.FC<Props> = ({ isOpen, onClose, imageUrl, item, userId, libraryImageId, carName, onCreated }) => {
  const cfg = item.apiConfig || {};
  // 価格FX・注文紐付けは数値のGoodsManagement id を使う（item.id は item_name の場合がある）
  const goodsId = item.goodsId ?? item.id;
  const allVariants: ProdigiVariant[] = cfg.variants || [];
  const publishedSkus: string[] = cfg.published_skus || [];

  // ポストカードのみメッセージ入力を許可（写真面に文字を焼き込む）
  const isPostcard = /post/i.test(`${cfg.product_type || ''} ${cfg.category || ''} ${item.displayName || ''}`)
    || allVariants.some(v => /(^|-)POST/i.test(v.sku || ''));

  // 公開バリエーションのみ（published_skusが空なら全部）
  const pubVariants = publishedSkus.length > 0
    ? allVariants.filter(v => publishedSkus.includes(v.sku))
    : allVariants;

  // 公開バリエーションから軸と選択肢を導出（購入可能な値だけ出す）
  const axisKeys = Object.keys(cfg.axes || {});
  const axisOptions: Record<string, string[]> = {};
  axisKeys.forEach(k => {
    const vals = Array.from(new Set(pubVariants.map(v => (v.attrs || {})[k]).filter(Boolean))) as string[];
    if (vals.length) axisOptions[k] = vals;
  });
  const activeAxes = Object.keys(axisOptions);
  // inch/cm は同じ寸法の別単位 → cm軸(キーに"(cm)")は、別の非cmサイズ軸がある時だけ隠す
  const hiddenAxes = new Set(
    activeAxes.filter(k => k.includes('(cm)') &&
      activeAxes.some(o => o !== k && !o.includes('(cm)') &&
        (o.includes('サイズ') || o.toLowerCase().includes('size'))))
  );
  const visibleAxes = activeAxes.filter(k => !hiddenAxes.has(k));

  const [selAttrs, setSelAttrs] = useState<Record<string, string>>({});
  const [printArea, setPrintArea] = useState<{ width?: number; height?: number; dpi?: number } | null>(null);
  const [loadingArea, setLoadingArea] = useState(false);
  const [tf, setTf] = useState<Transform>(DEFAULT_TF);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ merchandise_id: number; mockup_url: string } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showOrder, setShowOrder] = useState(false);
  const [message, setMessage] = useState('');   // ポストカードのメッセージ（写真面に合成）
  // 販売価格(JPY)は選択バリエーション原価×実FX＋上乗せ価格で動的算出（管理画面の≈¥と同じ）
  const [fxRates, setFxRates] = useState<Record<string, number>>({});
  const [profitMargin, setProfitMargin] = useState(0);
  const [priceReady, setPriceReady] = useState(false);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; cx: number; cy: number } | null>(null);

  // 初期選択（各軸の先頭値）
  useEffect(() => {
    if (!isOpen) return;
    const init: Record<string, string> = {};
    activeAxes.forEach(k => { init[k] = axisOptions[k][0]; });
    setSelAttrs(init);
    setTf(DEFAULT_TF);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item.id]);

  // 選択属性→バリエーション解決
  const selectedVariant: ProdigiVariant | undefined = (() => {
    if (!pubVariants.length) return undefined;
    const match = pubVariants.find(v =>
      visibleAxes.every(k => (v.attrs || {})[k] === selAttrs[k]));
    return match || pubVariants[0];
  })();

  // 画像ロード
  useEffect(() => {
    if (!isOpen || !imageUrl) return;
    setImgLoaded(false);
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => { imgRef.current = im; setImgLoaded(true); };
    im.onerror = () => { imgRef.current = im; setImgLoaded(true); }; // 表示は継続
    im.src = imageUrl;
  }, [isOpen, imageUrl]);

  // 選択SKUの推奨px取得
  useEffect(() => {
    if (!isOpen || !selectedVariant?.sku) return;
    let cancelled = false;
    setLoadingArea(true);
    setPrintArea(null);
    fetch(`${AISHA_API_BASE}/prodigi/print-area/${encodeURIComponent(selectedVariant.sku)}/`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d && d.success) setPrintArea({ width: d.width, height: d.height, dpi: d.dpi });
        else setPrintArea(null);
      })
      .catch(() => { if (!cancelled) setPrintArea(null); })
      .finally(() => { if (!cancelled) setLoadingArea(false); });
    return () => { cancelled = true; };
  }, [isOpen, selectedVariant?.sku]);

  // FXレート＋上乗せ価格を一度取得（販売価格のJPY算出用）
  useEffect(() => {
    if (!isOpen || !goodsId) return;
    let cancelled = false;
    setPriceReady(false);
    fetch(`${AISHA_API_BASE}/prodigi/goods/${goodsId}/live/`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d && d.success) {
          setFxRates(d.fx || {});
          setProfitMargin(Number(d.profit_margin) || 0);
        }
        setPriceReady(true);
      })
      .catch(() => { if (!cancelled) setPriceReady(true); });
    return () => { cancelled = true; };
  }, [isOpen, goodsId]);

  // 選択中バリエーションの販売価格(JPY)
  const unitPriceJpy = (() => {
    const cost = Number(selectedVariant?.price) || 0;
    const cur = selectedVariant?.currency || '';
    const rate = fxRates[cur] || 0;
    if (!rate) return null;   // FX未取得時はnull（¥0の誤表示を防ぐ）
    return Math.round(cost * rate) + profitMargin;
  })();

  // キャンバスの表示アスペクト比（推奨pxから。未取得は1:1）
  const areaW = printArea?.width || 1000;
  const areaH = printArea?.height || 1000;
  const aspect = areaW / areaH;
  const DISPLAY_W = 340;
  const displayW = aspect >= 1 ? DISPLAY_W : Math.round(DISPLAY_W * aspect);
  const displayH = aspect >= 1 ? Math.round(DISPLAY_W / aspect) : DISPLAY_W;

  // プレビュー描画
  const redraw = useCallback(() => {
    const c = canvasRef.current;
    const im = imgRef.current;
    if (!c || !im) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    drawComposite(ctx, c.width, c.height, im, tf, isPostcard ? message : undefined);
  }, [tf, isPostcard, message]);

  useEffect(() => { redraw(); }, [redraw, imgLoaded, displayW, displayH, printArea]);

  if (!isOpen) return null;

  // ドラッグ（表示座標→normalized）
  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, cx: tf.cx, cy: tf.cy };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = (e.clientX - dragRef.current.startX) / displayW;
    const dy = (e.clientY - dragRef.current.startY) / displayH;
    setTf(t => ({ ...t, cx: dragRef.current!.cx + dx, cy: dragRef.current!.cy + dy }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  // 「アイテムを作成」= 合成→GCS公開URL化→履歴保存（Printful/SUZURIと同じレール）
  const handleCreate = async () => {
    if (!imgRef.current || !selectedVariant?.sku) return;
    setCreating(true);
    setCreateError(null);
    try {
      const off = document.createElement('canvas');
      off.width = areaW;
      off.height = areaH;
      const octx = off.getContext('2d');
      if (!octx) throw new Error('canvas未対応');
      drawComposite(octx, areaW, areaH, imgRef.current, tf, isPostcard ? message : undefined);
      let dataUrl: string;
      try {
        dataUrl = off.toDataURL('image/png');
      } catch (e) {
        throw new Error('画像の書き出しに失敗しました（画像のCORS設定が必要）');
      }
      const variantLabel = visibleAxes.map(k => `${k}:${selAttrs[k]}`).join(' / ');
      const resp = await fetch(`${AISHA_API_BASE}/prodigi/create-merchandise/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || '',
          image_url: imageUrl,
          composited_base64: dataUrl,
          product_id: goodsId,
          sku: selectedVariant.sku,
          variant_label: variantLabel,
          price: unitPriceJpy ?? 0,
          library_image_id: libraryImageId || '',
          car_name: carName || '',
          product_title: item.displayName,
        }),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || '作成に失敗しました');
      setCreated({ merchandise_id: d.merchandise_id, mockup_url: d.mockup_url });
      if (onCreated) onCreated();
    } catch (e: any) {
      setCreateError(e?.message || '作成に失敗しました');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">Prodigi</span>
            <h2 className="text-lg font-bold text-gray-800">{item.displayName}</h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700">
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* 左: エディタ */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">画像の位置・サイズ調整</p>
            <div className="flex justify-center bg-gray-100 rounded-xl p-4">
              <div
                className="relative border-2 border-dashed border-purple-300 bg-white touch-none select-none"
                style={{ width: displayW, height: displayH, cursor: 'move' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
              >
                <canvas ref={canvasRef} width={displayW} height={displayH} className="absolute inset-0" />
                {(!imgLoaded || loadingArea) && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 text-gray-500 text-sm z-10">
                    <ArrowPathIcon className="w-7 h-7 animate-spin mb-1" />
                    <span>{loadingArea ? 'サイズ反映中…' : '読み込み中'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* スライダー */}
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs text-gray-500">拡大・縮小</span>
                <input type="range" min={0.2} max={2} step={0.01} value={tf.scale}
                  onChange={e => setTf(t => ({ ...t, scale: parseFloat(e.target.value) }))}
                  className="w-full" />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">回転</span>
                <input type="range" min={-180} max={180} step={1} value={tf.rot}
                  onChange={e => setTf(t => ({ ...t, rot: parseFloat(e.target.value) }))}
                  className="w-full" />
              </label>
              <button
                onClick={() => setTf(DEFAULT_TF)}
                className="text-xs text-purple-600 hover:underline"
              >位置をリセット</button>
            </div>

            {/* ポストカードのみ: メッセージ（写真面下部に焼き込む） */}
            {isPostcard && (
              <div className="mt-4">
                <label className="block">
                  <span className="text-xs font-semibold text-gray-700">メッセージ（任意・写真の下部に印刷）</span>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value.slice(0, 120))}
                    rows={2}
                    maxLength={120}
                    placeholder="例) お誕生日おめでとう！素敵な1年になりますように"
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-purple-400 focus:outline-none resize-none"
                  />
                </label>
                <div className="text-[11px] text-gray-400 mt-1 text-right">{message.length}/120</div>
              </div>
            )}

            <p className="text-[11px] text-gray-400 mt-2">
              {loadingArea ? '推奨サイズを取得中…'
                : printArea?.width
                  ? `印刷推奨サイズ: ${printArea.width}×${printArea.height}px${printArea.dpi ? ` (${printArea.dpi}dpi)` : ''}`
                  : '推奨サイズ未取得（1:1で表示中）'}
            </p>
          </div>

          {/* 右: バリエーション選択 + 価格 */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">バリエーション</p>
            {visibleAxes.length === 0 && (
              <p className="text-xs text-gray-400 mb-3">このアイテムに選択軸はありません</p>
            )}
            <div className="space-y-3">
              {visibleAxes.map(k => (
                <label key={k} className="block">
                  <span className="text-xs text-gray-500">{k}</span>
                  <select
                    value={selAttrs[k] || ''}
                    onChange={e => setSelAttrs(s => ({ ...s, [k]: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm text-gray-800"
                  >
                    {axisOptions[k].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
              ))}
            </div>

            <div className="mt-5 p-4 bg-gray-50 rounded-xl">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-gray-500">販売価格</span>
                <span className="text-2xl font-bold text-gray-900">
                  {unitPriceJpy != null ? `¥${unitPriceJpy.toLocaleString()}` : (priceReady ? '—' : '…')}
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">※送料は別途</p>
            </div>

            {created ? (
              <div className="mt-5 p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                <p className="text-green-700 font-bold">🎉 アイテムを作成しました！</p>
                <p className="text-[11px] text-gray-500 mt-1">アイテム履歴に保存されました。</p>
                <button
                  onClick={() => setShowOrder(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md"
                >
                  <ShoppingBagIcon className="w-5 h-5" /><span>このアイテムを購入する</span>
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={handleCreate}
                  disabled={creating || !selectedVariant?.sku}
                  className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {creating ? (
                    <><ArrowPathIcon className="w-5 h-5 animate-spin" /><span>作成中...</span></>
                  ) : (
                    <><ShoppingBagIcon className="w-5 h-5" /><span>この配置でアイテムを作成</span></>
                  )}
                </button>
                {createError && (
                  <p className="text-[11px] text-red-500 mt-2 text-center">{createError}</p>
                )}
                <p className="text-[11px] text-gray-400 mt-2 text-center">
                  ※ 作成するとアイテム履歴に保存されます。購入（決済）は次段で接続。
                </p>
              </>
            )}
          </div>
        </div>

        {/* フッター */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <span className="text-sm text-gray-500">AISHA × Prodigi</span>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 font-medium rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>閉じる</span>
          </button>
        </div>
      </div>

      {created && (
        <ProdigiOrderModal
          isOpen={showOrder}
          onClose={() => setShowOrder(false)}
          userId={userId || ''}
          merchandiseId={created.merchandise_id}
          sku={selectedVariant?.sku || ''}
          goodsId={typeof goodsId === 'number' ? goodsId : Number(goodsId) || undefined}
          copies={1}
          imageUrl={created.mockup_url}
          goodsName={(item as any)?.name || (item as any)?.product_title || 'Prodigi商品'}
          onCompleted={() => { if (onCreated) onCreated(); }}
        />
      )}
    </div>
  );
};

export default ProdigiPurchaseModal;
